/* eslint-disable react-hooks/exhaustive-deps */
import { PaymentStatus } from '@/api/enumerations';
import useGeneral from '@/hooks/useGeneral';
import { TabContext } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';

import {
  exportFinanceCSV,
  getAllHivePayments,
  getFinancialSummary,
} from '../../api/financial';
import {
  FinancialData,
  HivePaymentData,
  SortDirType,
  SortType,
} from '../../api/financial/types';
import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import { TitleTypography } from '../../components/UI/Typography';
import { Constants } from '../../constants/finance';
import { IconAccountBalanceMS } from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { useDebounce } from '../../hooks/useDebounce';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { Filters } from './components/Filters';
import { TabContent } from './components/TabContent';
import {
  TableContainer,
  FinancialBox,
  StyledTabPanel,
  StyledTabList,
  TabStyled,
  TabNotification,
} from './styles';

import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

type TabValue = 'actives' | 'history';

type FinancialDataKeys =
  | 'sum_paid'
  | 'sum_invoiced_and_pending'
  | 'count_pending';

const financialCards = [
  {
    title: Constants.totalPayed,
    color: '#0F6E56',
    valueKey: 'sum_paid' as FinancialDataKeys,
    prefix: 'R$ ',
  },
  {
    title: Constants.totalPending,
    color: '#707070',
    valueKey: 'sum_invoiced_and_pending' as FinancialDataKeys,
    prefix: 'R$ ',
  },
  {
    title: Constants.pending,
    color: '#B26E3D',
    valueKey: 'count_pending' as FinancialDataKeys,
    prefix: '',
  },
];

type TabFilters = {
  searchTerm: string;
  searchPeriod: string;
  searchStatus: PaymentStatus;
  pendingOnly: boolean;
  page: number;
  sort: { by: SortType; dir: SortDirType } | null;
};

const initialFilters: TabFilters = {
  searchTerm: '',
  searchPeriod: dayjs().subtract(1, 'month').format('YYYY-MM'),
  searchStatus: PaymentStatus.SELECT,
  pendingOnly: false,
  page: 1,
  sort: null,
};

export function Finance(): JSX.Element {
  const [selectedTab, setSelectedTab] = useState<TabValue>('actives');
  const [filtersByTab, setFiltersByTab] = useState<
    Record<TabValue, TabFilters>
  >({
    actives: { ...initialFilters },
    history: { ...initialFilters },
  });
  const [financialData, setFinancialData] = useState<
    FinancialData | undefined
  >();
  const [paymentsData, setPaymentsData] = useState<HivePaymentData[]>([]);
  const [hasPendingItems, setHasPendingItems] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const paymentsPerPage = 10;
  const currentPeriod = dayjs().subtract(1, 'month').format('YYYY-MM');
  const currentFilters = filtersByTab[selectedTab];
  const { searchTerm, searchPeriod, searchStatus, pendingOnly, page, sort } =
    currentFilters;
  const isHistoryTab = selectedTab === 'history';
  const formattedDate = searchPeriod
    ? dayjs(`${searchPeriod}-01`).format('MMMM YYYY')
    : dayjs().subtract(1, 'month').format('MMMM YYYY');

  const { openSnackbar } = useContext(GlobalContext);
  const { getErrorMessage, logDevError } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { handleDownloadXlsx } = useGeneral();
  const debouncedSearch = useDebounce(searchTerm);

  const updateCurrentFilter = useCallback(
    <K extends keyof TabFilters>(key: K, value: TabFilters[K]) => {
      setFiltersByTab((prev) => ({
        ...prev,
        [selectedTab]: {
          ...prev[selectedTab],
          [key]: value,
        },
      }));
    },
    [selectedTab]
  );

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: TabValue
  ): void => {
    setFiltersByTab((prev) => ({
      ...prev,
      [newValue]: { ...initialFilters },
    }));
    setSelectedTab(newValue);
  };

  const getPendingCallback = useCallback(async () => {
    try {
      const response = await getAllHivePayments({
        page: 1,
        size: paymentsPerPage,
        period: currentPeriod,
        pendingOnly: true,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      if (response.detail.total_pages)
        setTotalPages(response.detail.total_pages);

      setHasPendingItems(response.data.length > 0);
    } catch (error) {
      logDevError(error);
    }
  }, []);

  const getDataCallback = useCallback(async () => {
    const searchReferencePeriod = isHistoryTab
      ? searchPeriod || currentPeriod
      : currentPeriod;

    try {
      setLoading(true);

      const financialResponse = await getFinancialSummary({
        search: debouncedSearch,
        period: searchReferencePeriod,
        ...(isHistoryTab && pendingOnly && { pendingOnly: true }),
      });

      if (financialResponse.detail.description)
        throw new Error(financialResponse.detail.description);

      if (!financialResponse.data)
        throw new Error('Algo deu errado, tente novamente.');

      setFinancialData(financialResponse.data);

      const paymentsParams = {
        page,
        size: paymentsPerPage,
        search: debouncedSearch,
        period: searchReferencePeriod,
        ...(isHistoryTab && pendingOnly
          ? { pendingOnly: true }
          : {
              ...(searchStatus !== PaymentStatus.SELECT && {
                status: searchStatus,
              }),
            }),
        ...(sort && { sortBy: sort.by, sortDir: sort.dir }),
      };

      const response = await getAllHivePayments(paymentsParams);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      if (response.detail.total_pages)
        setTotalPages(response.detail.total_pages);

      setPaymentsData(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    searchPeriod,
    searchStatus,
    pendingOnly,
    isHistoryTab,
    currentPeriod,
    sort,
  ]);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length > 0 && page !== 1) {
      updateCurrentFilter('page', 1);
    }

    if (!debouncedSearch && page !== 1) {
      updateCurrentFilter('page', 1);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  useEffect(() => {
    getPendingCallback();
  }, []);

  const handleNextPage = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    updateCurrentFilter('page', value);
  };

  const getCSVFile = useCallback(async () => {
    const searchReferencePeriod = isHistoryTab
      ? searchPeriod || currentPeriod
      : currentPeriod;

    try {
      setLoadingExport(true);

      const paymentsParams = {
        search: debouncedSearch,
        period: searchReferencePeriod,
        ...(isHistoryTab && pendingOnly
          ? { pendingOnly: true }
          : {
              ...(searchStatus !== PaymentStatus.SELECT && {
                status: searchStatus,
              }),
            }),
        ...(sort && { sortBy: sort.by, sortDir: sort.dir }),
      };

      const response = await exportFinanceCSV(paymentsParams);

      if (response.ok) {
        handleDownloadXlsx(response);
      } else {
        throw new Error('Não foi possível fazer o download, tente novamente.');
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingExport(false);
    }
  }, [
    debouncedSearch,
    searchPeriod,
    searchStatus,
    pendingOnly,
    isHistoryTab,
    currentPeriod,
    sort,
  ]);

  const handleSort = (column: SortType): void => {
    setFiltersByTab((prev) => {
      const currentSort = prev[selectedTab].sort;
      let newSort: typeof currentSort;

      if (currentSort?.by === column) {
        if (currentSort.dir === 'desc') newSort = { by: column, dir: 'asc' };
        else {
          newSort = null;
        }
      } else {
        newSort = { by: column, dir: 'desc' };
      }

      return {
        ...prev,
        [selectedTab]: {
          ...prev[selectedTab],
          sort: newSort,
        },
      };
    });
  };

  return (
    <GridMain>
      <TitleBox flexDirection="column" alignItems="start !important">
        <TitleTypography>
          {IconAccountBalanceMS}
          {Constants.title}
        </TitleTypography>
        <Typography mt="12px">{Constants.subtitle}</Typography>
      </TitleBox>
      <Grid container spacing={4}>
        {financialCards.map((card) => (
          <Grid item xs={4} key={card.title}>
            <FinancialBox>
              {card.title}
              <Typography fontSize="14px">
                {pendingOnly ? Constants.allHistory : formattedDate}
              </Typography>
              <Typography
                fontWeight={600}
                fontSize="24px"
                color={financialData ? card.color : 'inherit'}
              >
                {financialData
                  ? `${card.prefix}${financialData[card.valueKey]}`
                  : '—'}
              </Typography>
            </FinancialBox>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: '32px' }}>
        <TabContext value={selectedTab}>
          <StyledTabList onChange={handleTabChange}>
            <TabStyled label="Ativos" value="actives" />
            <TabStyled
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Histórico</span>
                  {hasPendingItems && <TabNotification />}
                </Box>
              }
              value="history"
            />
          </StyledTabList>
          <TableContainer>
            <Filters
              searchStatus={searchStatus}
              setSearchStatus={(value) =>
                updateCurrentFilter('searchStatus', value)
              }
              setSearchPeriod={(value) =>
                updateCurrentFilter('searchPeriod', value)
              }
              setSearchTerm={(value) =>
                updateCurrentFilter('searchTerm', value)
              }
              pendingOnly={pendingOnly}
              setPendingOnly={(value) =>
                updateCurrentFilter('pendingOnly', value)
              }
              isHistoryTab={isHistoryTab}
              loadingExport={loadingExport}
              initialDate={currentFilters.searchPeriod}
              getCSVFile={getCSVFile}
            />
            <StyledTabPanel value="actives">
              <TabContent
                loading={loading}
                page={page}
                totalPages={totalPages}
                handleNextPage={handleNextPage}
                paymentsData={paymentsData}
                getDataCallback={getDataCallback}
                handleSort={handleSort}
                sort={sort}
              />
            </StyledTabPanel>
            <StyledTabPanel value="history">
              <TabContent
                loading={loading}
                page={page}
                totalPages={totalPages}
                handleNextPage={handleNextPage}
                paymentsData={paymentsData}
                getDataCallback={getDataCallback}
                handleSort={handleSort}
                sort={sort}
                formattedDate={formattedDate}
                pendingOnly={pendingOnly}
              />
            </StyledTabPanel>
          </TableContainer>
        </TabContext>
      </Box>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
